import { ChallengeStatus, ScoreFieldTypes } from "../../enums";
import { boolOrIntCodec, nonEmptyArray, timestampCodec } from "../../validation/codecs";
import { enumCodec, mixed, optional, required, t } from "../../validation/validator";
import { CallResponseSchema, FunctionsErrorCodeCodec } from "../common/callResponseSchema";
import { commonRewardFields, RewardRecordSchema } from "../rewards/rewardSchemas";
import { ScenarioConfigResponseSchema, ScenarioConfigSchema } from "../scenarios/scenarioSchemas";
import { TagRecordSchema } from "../tags/tagSchemas";
import { ChallengeLocalizationRequestSchema, ChallengeLocalizationResponseSchema } from "./challengeLocalizationSchemas";

/****************************************************
 * ChallengeTarget
 */
const challengeTargetFields = {
  reward_id: t.number,
  [ScoreFieldTypes.LABELS_MADE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.LABELS_SUCCEEDED]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.RECEIVALS_MADE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.RECEIVALS_SUCCEEDED]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.TACKLES_MADE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.TACKLES_SUCCEEDED]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.SHOTS_MADE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.SHOTS_SUCCEEDED]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.PASSES_MADE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.PASSES_SUCCEEDED]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.PASSES_SCORE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.PASSES_PACKING_RATE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.AWARENESS_SCORE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.TOTAL_SCORE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.GAMEPLAY_DURATION]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.SCAN_SCORE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.SCAN_RATE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.SCAN_TIMING]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.CRITICAL_SCAN_SCORE]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.CRITICAL_SCANS]: t.union([t.number, t.undefined, t.null]),
  [ScoreFieldTypes.BYB_IQ]: t.union([t.number, t.undefined, t.null]),
};

// Schemas
export const ChallengeTargetSchema = t.type(challengeTargetFields);
export const UpdateChallengeTargetSchema = t.type({
  ...challengeTargetFields,
  id: t.number,
});
export const ChallengeTargetRecordSchema = t.type({
  ...challengeTargetFields,
  id: t.number,
  created_at: timestampCodec,
  updated_at: t.union([timestampCodec, t.undefined, t.null]),
  challenge_id: t.number,
});
export const ChallengeTargetResponseSchema = t.type({
  ...challengeTargetFields,
  id: t.number,
});
export const ChallengeTargetInsertRecordSchema = t.type({
  ...challengeTargetFields,
  updated_at: t.union([timestampCodec, t.undefined, t.null]),
  challenge_id: t.number,
});
// Types
export type ChallengeTargetFields = t.TypeOf<typeof ChallengeTargetSchema>;
export type ChallengeTargetResponse = t.TypeOf<typeof ChallengeTargetResponseSchema>;
export type ChallengeTargetRecord = t.TypeOf<typeof ChallengeTargetRecordSchema>;
export type ChallengeTargetInsertRecord = t.TypeOf<typeof ChallengeTargetInsertRecordSchema>;
export type ChallengeTargetRequest = t.TypeOf<typeof ChallengeTargetSchema>;
export type UpdateChallengeTargetRequest = t.TypeOf<typeof UpdateChallengeTargetSchema>;

/****************************************************
 * Challenge
 */
const commonChallengeFields = {
  title: t.string,
  subtitle: t.string,
  goal: t.string,
  description: t.string,
  image_url: t.union([t.string, t.undefined, t.null]),
  video_url: t.union([t.string, t.undefined, t.null]),
  published: t.union([boolOrIntCodec, t.undefined]),
  unlocked_at: t.union([t.number, t.undefined, t.null]),
  randomize_scenarios: t.union([boolOrIntCodec, t.undefined, t.null]),
  scenario_sample_size: t.union([t.number, t.undefined, t.null]),
  start_at: t.union([timestampCodec, t.undefined, t.null]),
  end_at: t.union([timestampCodec, t.undefined, t.null]),
  all_challenges: t.union([t.array(t.any), t.undefined, t.null]),
};
// Schemas
export const ChallengeSchema = t.type({ ...commonChallengeFields });
export const ChallengeRecordInsertSchema = t.type({
  updated_at: t.union([timestampCodec, t.undefined, t.null]),
  ...commonChallengeFields,
});
export const ChallengeRecordSchema = t.type({
  id: t.number,
  created_at: timestampCodec,
  updated_at: t.union([timestampCodec, t.undefined, t.null]),
  ...commonChallengeFields,
});

const ChallengeResponseFields = {
  id: t.number,
  created_at: timestampCodec,
  updated_at: t.union([timestampCodec, t.undefined, t.null]),
  scenarios: t.array(ScenarioConfigResponseSchema),
  targets: t.array(ChallengeTargetRecordSchema),
  rewards: t.array(RewardRecordSchema),
  status: t.union([t.string, t.undefined, t.null]),
};

// Response object to be sent to the client (includes targets, scenarios & rewards)
export const ChallengeResponseSchema = t.intersection([
  t.type(ChallengeResponseFields),
  t.partial({
    ...commonChallengeFields,
    last_played_at: t.union([timestampCodec, t.undefined, t.null]),
    tags: t.union([t.array(TagRecordSchema), t.null]),
  }),
]);

// Types
export type ChallengeResponse = t.TypeOf<typeof ChallengeResponseSchema>;
export type ChallengeRecord = t.TypeOf<typeof ChallengeRecordSchema>;
export type ChallengeInsertRecord = t.TypeOf<typeof ChallengeRecordInsertSchema>;

/****************************************************
 * CreateChallenge
 */
// Schemas
export const CreateChallengeSchema = t.type({
  targets: nonEmptyArray(t.type(challengeTargetFields)),
  scenarios: nonEmptyArray(ScenarioConfigSchema),
  tags: t.union([t.array(t.string), t.undefined]),
  ...commonChallengeFields,
});
// Types
export type CreateChallengeRequest = t.TypeOf<typeof CreateChallengeSchema>;

/****************************************************
 * UpdateChallenge
 */
// Schemas
export const UpdateChallengeSchema = t.type({
  id: t.number,
  scenarios: t.union([nonEmptyArray(ScenarioConfigSchema), t.undefined]),
  targets: t.union([nonEmptyArray(t.type(challengeTargetFields)), t.undefined]),
  tags: t.union([t.array(t.string), t.undefined]),
  ...commonChallengeFields,
  // Override common fields to allow undefined
  title: t.union([t.string, t.undefined]),
  subtitle: t.union([t.string, t.undefined]),
  goal: t.union([t.string, t.undefined]),
  description: t.union([t.string, t.undefined]),
});
// Types
export type UpdateChallengeRequest = t.TypeOf<typeof UpdateChallengeSchema>;

/****************************************************
 * GetChallenges
 */
// Schemas
export const GetChallengesSchema = mixed([
  required({
    // Required fields
    user_id: t.string,
  }),
  optional({
    // Optional fields
    ids: t.union([t.array(t.number), t.undefined]),
    unpublished_allowed: t.union([t.boolean, t.undefined]),
    include_tags: t.union([t.array(t.string), t.undefined]),
    exclude_tags: t.union([t.array(t.string), t.undefined]),
    limit: t.union([t.number, t.undefined]),
    filters: t.union([
      t.partial({
        statuses: t.union([t.array(enumCodec(ChallengeStatus)), t.undefined]),
        starts_after: t.union([timestampCodec, t.undefined]),
        ends_before: t.union([timestampCodec, t.undefined]),
        min_rating: t.union([t.number, t.undefined]),
        max_rating: t.union([t.number, t.undefined]),
      }),
      t.undefined,
    ]),
    order_by: t.union([
      t.array(
        t.type({
          field: t.string,
          direction: t.union([t.literal("asc"), t.literal("desc")]),
        })
      ),
      t.undefined,
    ]),
    // include_fields: t.union([t.array(t.string), t.undefined]),
    include_fields: t.union([t.array(t.keyof(ChallengeResponseFields)), t.undefined]),
  }),
]);

// Types
export type GetChallengesRequest = t.TypeOf<typeof GetChallengesSchema>;

export const GetChallengeResultsSchema = t.type({
  user_id: t.string,
  challenge_id: t.number,
});

export type GetChallengeResultsRequest = t.TypeOf<typeof GetChallengeResultsSchema>;

// Targets are cleaned of null fields by getChallengeResults.
const ChallengeSessionTargetSchema = t.type({
  ...challengeTargetFields,
  id: t.number,
  challenge_id: t.number,
});

export const ChallengeSessionRewardSchema = t.type({
  id: t.number,
  name: commonRewardFields.name,
  description: commonRewardFields.description,
  value: commonRewardFields.value,
  type: commonRewardFields.type,
  scarcity: commonRewardFields.scarcity,
  image_url: commonRewardFields.image_url,
  challenge_id: t.number,
  created_at: timestampCodec,
});

// The endpoint averages every score field except gameplay_duration.
const challengeResultFields = Object.fromEntries(
  Object.values(ScoreFieldTypes)
    .filter(field => field !== ScoreFieldTypes.GAMEPLAY_DURATION)
    .map(field => [field, t.union([t.number, t.null])])
) as Record<Exclude<ScoreFieldTypes, ScoreFieldTypes.GAMEPLAY_DURATION>, t.UnionC<[t.NumberC, t.NullC]>>;

export const ChallengeSessionSchema = t.type({
  status: enumCodec(ChallengeStatus),
  play_session: t.type({
    id: t.number,
    type: t.string,
    game_mode: t.string,
    play_position: t.union([t.string, t.null]),
    experience_gained: t.number,
    is_completed: boolOrIntCodec,
    user_rating: t.union([t.number, t.null]),
    game_speed: t.union([t.number, t.null]),
    created_at: timestampCodec,
    updated_at: t.union([timestampCodec, t.null]),
  }),
  rewards: t.union([t.array(ChallengeSessionRewardSchema), t.null]),
  targets: t.union([ChallengeSessionTargetSchema, t.null]),
  next_targets: t.union([ChallengeSessionTargetSchema, t.null]),
  results: t.type(challengeResultFields),
});

export type ChallengeSession = t.TypeOf<typeof ChallengeSessionSchema>;
export type ChallengeSessionReward = t.TypeOf<typeof ChallengeSessionRewardSchema>;

export const ChallengeResultsResponseSchema = t.type({
  challenge_id: t.union([t.number, t.null]),
  // The SQL query does not select a top-level created_at, so it can be absent.
  created_at: t.union([timestampCodec, t.undefined, t.null]),
  challenge_sessions: t.array(ChallengeSessionSchema),
});

export type ChallengeResultsResponse = t.TypeOf<typeof ChallengeResultsResponseSchema>;

// Sessions

// Create Session
export const CreateChallengeSessionSchema = t.type({
  account_id: t.union([t.number, t.null]),
  challenge_id: t.number,
});

// Insert User Session
export const ChallengeUserSessionsInsertSchema = t.type({
  user_id: t.number,
  challenge_id: t.number,
  play_session_id: t.number,
  status: enumCodec(ChallengeStatus),
});

export type ChallengeUserSessionsInsertRecord = t.TypeOf<typeof ChallengeUserSessionsInsertSchema>;

// Client API
export const ChallengeApis = {
  Challenges: {
    ChallengeLocalization: {
      Name: "challenges-challengeLocalization",
      Request: ChallengeLocalizationRequestSchema,
      Response: ChallengeLocalizationResponseSchema,
    },
    CreateChallenge: {
      Name: "challenges-createChallenge",
      Request: CreateChallengeSchema,
      Response: CallResponseSchema,
    },
    UpdateChallenge: {
      Name: "challenges-updateChallenge",
      Request: UpdateChallengeSchema,
      Response: CallResponseSchema,
    },
    GetChallenges: {
      Name: "challenges-getChallenges",
      Request: GetChallengesSchema,
      Response: mixed([required({ data: t.array(ChallengeResponseSchema) }), CallResponseSchema]),
    },
    GetChallengeResults: {
      Name: "challenges-getChallengeResults",
      Request: GetChallengeResultsSchema,
      Response: t.intersection([
        t.type({ code: FunctionsErrorCodeCodec, data: t.union([ChallengeResultsResponseSchema, t.null]) }),
        t.partial({ message: t.string }),
      ]),
    },
    GetNextChallenge: {
      Name: "challenges-getNextChallenge",
      Request: mixed([required({ user_rating: t.number }), GetChallengesSchema]),
      Response: mixed([required({ data: t.array(ChallengeResponseSchema) }), CallResponseSchema]),
    },
  },
};
